export type DateMarkPrecision = 'DAY_MONTH_YEAR' | 'MONTH_YEAR' | 'YEAR';

export interface ParsedDateMarkValue {
  precision: DateMarkPrecision;
  value: string;
}

/**
 * All. X punto 1(a), Reg. (UE) 1169/2011:
 * - GG/MM «sufficiente» per una durata non superiore a 3 mesi;
 * - MM/AAAA «sufficiente» per una durata superiore a 3 mesi e non superiore a 18 mesi;
 * - AAAA sufficiente per una durata superiore a 18 mesi.
 *
 * «Sufficiente» sets minimum precision; richer values remain valid. A missing
 * duration is handled conservatively by requiring the complete date.
 */
export function minimumDateMarkPrecision(shelfLifeMonths: number | null): DateMarkPrecision {
  if (shelfLifeMonths === null || shelfLifeMonths <= 3) {
    return 'DAY_MONTH_YEAR';
  }
  if (shelfLifeMonths <= 18) {
    return 'MONTH_YEAR';
  }
  return 'YEAR';
}

export function parseDateMarkValue(value: string): ParsedDateMarkValue | null {
  if (/^\d{4}$/.test(value)) {
    return value.startsWith('0000') ? null : { precision: 'YEAR', value };
  }

  const monthMatch = /^(\d{4})-(\d{2})$/.exec(value);
  if (monthMatch) {
    const [, year, month] = monthMatch;
    if (year === undefined || month === undefined || year === '0000') {
      return null;
    }
    const monthNumber = Number(month);
    return monthNumber >= 1 && monthNumber <= 12 ? { precision: 'MONTH_YEAR', value } : null;
  }

  const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!dayMatch) {
    return null;
  }
  const [, year, month, day] = dayMatch;
  if (year === undefined || month === undefined || day === undefined || year === '0000') {
    return null;
  }
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  const daysInMonth = new Date(Date.UTC(Number(year), monthNumber, 0)).getUTCDate();
  return monthNumber >= 1 && monthNumber <= 12 && dayNumber >= 1 && dayNumber <= daysInMonth
    ? { precision: 'DAY_MONTH_YEAR', value }
    : null;
}
